import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Pagination } from '@mui/material';
import { db } from '../../firebaseConfig';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import CommentItem from './CommentItem';
import { useSigilContext } from '../../context/sigilContext';

const CommentList = () => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 5;
    const { i18n } = useSigilContext();
    const translate = i18n?.forum || {};

    useEffect(() => {
        const q = query(collection(db, 'comments'), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const commentsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Organize into tree structure
            const commentMap = {};
            const rootComments = [];

            // First pass: create map and identify roots
            commentsData.forEach(comment => {
                comment.replies = [];
                commentMap[comment.id] = comment;
            });

            // Second pass: link replies to parents
            commentsData.forEach(comment => {
                if (comment.parentId) {
                    if (commentMap[comment.parentId]) {
                        commentMap[comment.parentId].replies.push(comment);
                    } else {
                        rootComments.push(comment);
                    }
                } else {
                    rootComments.push(comment);
                }
            });

            // Sort replies chronologically
            Object.values(commentMap).forEach(comment => {
                comment.replies.sort((a, b) => {
                    if (!a.createdAt || !b.createdAt) return 0;
                    return a.createdAt.seconds - b.createdAt.seconds;
                });
            });

            setComments(rootComments);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching comments:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Reset to page 1 when comments change
    useEffect(() => {
        setPage(1);
    }, [comments.length]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Pagination Logic
    const count = Math.ceil(comments.length / ITEMS_PER_PAGE);
    const paginatedComments = comments.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    );

    return (
        <Box>
            {comments.length === 0 ? (
                <Typography variant="body1" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
                    {translate.emptyComments || 'Aún no hay comentarios. ¡Sé el primero en escribir uno!'}
                </Typography>
            ) : (
                <>
                    {paginatedComments.map(comment => (
                        <CommentItem key={comment.id} comment={comment} replies={comment.replies} />
                    ))}

                    {count > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                            <Pagination
                                count={count}
                                page={page}
                                onChange={handleChangePage}
                                variant="outlined"
                                color="primary"
                                size="large"
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        color: 'white',
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                        }
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: 'rgba(255,255,255,0.2) !important',
                                        borderColor: 'white !important',
                                    }
                                }}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default CommentList;
